/**
 * `useAsOfTree` — the data layer for the B3.3 rewind (the second hero). A
 * TanStack Query over the already-merged read endpoint:
 *
 *     GET /api/teams/{teamId}/tasks/tree/as-of?at=<ISO datetime>
 *       → { at, nodes: NodeStateAsOf[] }
 *
 * The server replays the event log to `at` and returns the node set as it
 * stood that instant: nodes created after `at` are absent; nodes archived
 * before `at` are present with `archived=true`. At `at = now` it equals the
 * live tree.
 *
 * Issue #764 (Phase 3, temporal frontend). Kept deliberately separate from
 * the rendering (the clean query/view split §A7): the hook owns fetching +
 * caching keyed by `(teamId, atISO)`; the rewind view consumes `nodes` (via
 * the adapter) and never calls `apiFetch` directly. Each distinct date is its
 * own cache entry, so scrubbing back to an already-seen day is instant.
 *
 * Graceful degradation (the endpoint may not be deployed in a given
 * environment yet): retry is disabled and `isError` is exposed so the view
 * renders a calm "Rewind isn't available yet" state rather than crashing or
 * spinning. An empty node set (an early date before any node existed) is a
 * valid result, not an error — the view shows the origin/empty state.
 *
 * Isolation: imports only the generic `apiFetch` + the generated schema
 * types — no scheduler coupling (`taskmap ⊥ scheduler`).
 */

import { useQuery } from "@tanstack/react-query";

import { apiFetch } from "../../api/client";
import type { components } from "../../api/schema.gen";
import type { NodeStateAsOf } from "./asOfAdapter";

type AsOfResponse = components["schemas"]["AsOfResponse"];

/** Cache key for a team's as-of tree at a given instant. Exported for tests. */
export const AS_OF_TREE_KEY = (teamId: string, atISO: string) =>
  ["task-tree-as-of", teamId, atISO] as const;

const STALE_TIME_MS = 60_000;

export interface UseAsOfTreeResult {
  /** The node set as it stood at `atISO` (replay shape). Empty while loading / on error. */
  readonly nodes: NodeStateAsOf[];
  /** The server-resolved instant the projection was computed for (echoed `at`), or null. */
  readonly resolvedAt: string | null;
  readonly isLoading: boolean;
  /** True on any fetch failure (incl. a not-yet-deployed 404). */
  readonly isError: boolean;
}

/**
 * Fetch the team's tree as it stood at `atISO`. `enabled` only when both a
 * team and a chosen instant are present (i.e. the rewind surface is open and
 * a date has been picked) — no request fires for the live editor. Degrades to
 * an empty node set on a non-array / errored body.
 */
export function useAsOfTree(
  teamId: string | undefined,
  atISO: string | undefined,
): UseAsOfTreeResult {
  const query = useQuery({
    queryKey: AS_OF_TREE_KEY(teamId ?? "", atISO ?? ""),
    enabled: Boolean(teamId) && Boolean(atISO),
    staleTime: STALE_TIME_MS,
    // No retry: a not-yet-deployed endpoint fails fast to the calm
    // "rewind unavailable" state rather than spinning.
    retry: false,
    queryFn: () =>
      apiFetch<AsOfResponse>(
        `/api/teams/${teamId}/tasks/tree/as-of?at=${encodeURIComponent(atISO as string)}`,
      ),
  });

  const nodes = Array.isArray(query.data?.nodes) ? query.data.nodes : [];
  return {
    nodes,
    resolvedAt: query.data?.at ?? null,
    isLoading: query.isLoading,
    isError: query.isError,
  };
}
